class Cache {
  constructor({ timeToLiveMs, capacity, evictAfterMs } = {}) {
    // We store our elements in a FIFO queue in order to easily be able to remove
    // the oldest element in case of capacity overflows
    this.elements = [];
    // We store the offset from key to value during insertions in order to easily
    // be able to extract a value from the elements array
    this.keyToOffset = /* @__PURE__ */ new Map();
    this.amountInserted = 0;
    this.evictExpiredElements = () => {
      for (let i = 0; i < this.elements.length; i++) {
        const element = this.elements[i];
        if (element && this.isElementExpired(element, this.evictAfterMs)) {
          this.freeByIndex(i);
        }
      }
    };
    this.capacity = capacity;
    this.timeToLiveMs = timeToLiveMs;
    this.evictAfterMs = evictAfterMs;
    if (evictAfterMs) {
      setInterval(this.evictExpiredElements, evictAfterMs * 0.5);
    }
  }
  set(key, value) {
    if (this.elements.length === this.capacity) {
      this.removeOldest();
    }
    const offsetToExistingKey = this.keyToOffset.get(key);
    if (offsetToExistingKey !== void 0) {
      const index = this.getIndexByKeyOffset(offsetToExistingKey);
      const element = this.elements[index];
      if (element) {
        element.insertedAt = Date.now();
        element.value = value;
      }
      return;
    }
    this.elements.unshift({
      insertedAt: Date.now(),
      key,
      value
    });
    this.keyToOffset.set(key, this.amountInserted++);
  }
  get(key) {
    const offset = this.keyToOffset.get(key);
    if (offset === void 0) {
      return;
    }
    const index = this.getIndexByKeyOffset(offset);
    const element = this.elements[index];
    if (!element) {
      return;
    }
    return {
      value: element.value,
      isStale: this.isElementExpired(element)
    };
  }
  has(key) {
    const element = this.get(key);
    return {
      has: !!element,
      isStale: (element == null ? void 0 : element.isStale) ?? false
    };
  }
  delete(key) {
    if (this.keyToOffset.has(key)) {
      this.freeByIndex(
        this.getIndexByKeyOffset(this.keyToOffset.get(key))
      );
    }
  }
  /**
   * By subtracting the offset of the current element (i.e. the amount of
   * elements when the item was inserted) from the amount of elements currently
   * in the FIFO queue we then have the index of our desired element.
   */
  getIndexByKeyOffset(offset) {
    return this.amountInserted - offset - 1;
  }
  /**
   * Determines if an element inside the cache has surpassed a given threshold,
   * defined by the compareAgainst argument. By default it will compare with an
   * elements timeToLive in order to determine staleness.
   */
  isElementExpired(element, compareAgainst = this.timeToLiveMs) {
    return !!compareAgainst && Date.now() - element.insertedAt > compareAgainst;
  }
  removeOldest() {
    const removedElement = this.elements.pop();
    if (removedElement) {
      this.keyToOffset.delete(removedElement.key);
    }
  }
  /**
   * Instead of generally deleting elements from our FIFO queue we simply
   * remove it the keyToOffset mapping (To make it appear non-existent) and set
   * its value in our queue to undefined in order to allow garbage collection
   * to eventually remove the element.
   *
   * This is done in order to ensure the correctness of element offsets.
   */
  freeByIndex(index) {
    const elementToFree = this.elements[index];
    if (elementToFree) {
      this.keyToOffset.delete(elementToFree.key);
      this.elements[index] = void 0;
    }
  }
}

export { Cache };
//# sourceMappingURL=cache.js.map
